import { useEffect, useRef, useState, type CSSProperties } from "react";
import { api } from "../api/client";
import type { CustomTheme, Gradient, Palette, PaletteVariant } from "../api/types";
import { useErrorHandler } from "../auth/AuthContext";
import {
  CARD_OPACITY_SLIDER_MIN,
  deriveVars,
  MAX_CARD_OPACITY,
  type Scheme,
} from "../lib/palette";
import { exportTheme } from "../lib/themeShare";
import { ErrorMessage, Field, Modal, Segmented } from "./common";

/** How long the "Copied!" note lingers, matching AppearanceModal's "Saved!". */
const COPIED_MS = 2000;

/**
 * Where a member builds a palette of their own.
 *
 * Two ideas make this small enough to be usable by a nine-year-old.
 *
 * The first is that light and dark are edited separately but never at the same
 * time. Which scheme a person sees is the operating system's call, not the
 * app's, so a palette has to answer for both — but showing both at once doubles
 * every control on screen. The tab switches which one is being edited *and*
 * which one the preview shows, so the two halves are always in agreement about
 * what is being looked at.
 *
 * The second is that six colours and a gradient is the whole palette. The app
 * paints with twenty-eight variables; lib/palette.ts derives the other
 * twenty-one, because a border and a scrim are not opinions anybody holds.
 */

const MIN_STOPS = 2;
const MAX_STOPS = 6;

/** A new palette starts as a copy of Blueberry, so nothing is ever blank. */
function startingPalette(): Palette {
  return {
    light: {
      brand: "#5b6cff",
      spend: "#f4a261",
      save: "#2a9d8f",
      celebrate: "#16a06a",
      surface: "#ffffff",
      text: "#1a1f2e",
      background: {
        angle: 160,
        stops: [
          { color: "#f6f7fb", position: 0 },
          { color: "#eef0ff", position: 100 },
        ],
      },
    },
    dark: {
      brand: "#8b98ff",
      spend: "#f4a261",
      save: "#2a9d8f",
      celebrate: "#1fb87c",
      surface: "#1c1f2b",
      text: "#eef0f6",
      background: {
        angle: 160,
        stops: [
          { color: "#14161f", position: 0 },
          { color: "#1b1f33", position: 100 },
        ],
      },
    },
  };
}

/** The six seeds, in the order they are worth thinking about. */
const SEEDS: { key: keyof Omit<PaletteVariant, "background">; label: string }[] =
  [
    { key: "brand", label: "Main color" },
    { key: "spend", label: "Spend" },
    { key: "save", label: "Save" },
    { key: "celebrate", label: "Good news" },
    { key: "surface", label: "Cards" },
    { key: "text", label: "Words" },
  ];

export function ThemeEditor({
  editing,
  initial,
  onClose,
  onSaved,
}: {
  /** The palette being changed, or null to build a new one. */
  editing: CustomTheme | null;
  /**
   * A starting point for a new palette — from Import — rather than the blank
   * default. Ignored when `editing` is set: an existing palette's own fields
   * always win.
   */
  initial?: { name: string; description: string; palette: Palette };
  onClose: () => void;
  /** Called after a successful save or delete, with the theme to wear (if any). */
  onSaved: (wear?: string) => void;
}) {
  const toMessage = useErrorHandler();

  const [name, setName] = useState(editing?.name ?? initial?.name ?? "My colors");
  const [description, setDescription] = useState(
    editing?.description ?? initial?.description ?? "",
  );
  const [palette, setPalette] = useState<Palette>(
    editing?.palette ?? initial?.palette ?? startingPalette(),
  );
  const [scheme, setScheme] = useState<Scheme>("light");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const variant = palette[scheme];

  // Absent means opaque. Resolving it here rather than defaulting the field
  // keeps an untouched palette from acquiring a cardOpacity it never set, which
  // is what lets palettes made before this feature stay byte-identical.
  const cardOpacity = variant.cardOpacity ?? MAX_CARD_OPACITY;

  // The slider normally stops at CARD_OPACITY_SLIDER_MIN, where text is still
  // comfortably readable. An imported palette may legitimately sit below that,
  // though — the floor is this editor's opinion, not a rule the format
  // enforces — so the track stretches to fit rather than snapping someone
  // else's deliberate choice back up the moment they open it.
  const sliderMin = Math.min(CARD_OPACITY_SLIDER_MIN, cardOpacity);

  /** Rewrites the variant currently being edited, leaving the other alone. */
  function patch(next: Partial<PaletteVariant>) {
    setPalette((prev) => ({ ...prev, [scheme]: { ...prev[scheme], ...next } }));
  }

  /**
   * Sets or clears the card opacity on the variant being edited.
   *
   * Clearing genuinely removes the key rather than setting it to undefined, so
   * an exported palette has no `cardOpacity` line at all — the difference
   * between "opaque because that was chosen" and "never chose", which the
   * nullable field exists to preserve.
   */
  function setCardOpacity(next: number | undefined) {
    setPalette((prev) => {
      const { cardOpacity: _cleared, ...rest } = prev[scheme];
      return {
        ...prev,
        [scheme]: next === undefined ? rest : { ...rest, cardOpacity: next },
      };
    });
  }

  function patchGradient(next: Partial<Gradient>) {
    patch({ background: { ...variant.background, ...next } });
  }

  function setStop(index: number, next: Partial<{ color: string; position: number }>) {
    const stops = variant.background.stops.map((stop, i) =>
      i === index ? { ...stop, ...next } : stop,
    );
    patchGradient({ stops });
  }

  function addStop() {
    const stops = [...variant.background.stops];
    const last = stops[stops.length - 1];
    const previous = stops[stops.length - 2];
    if (!last) return;

    // The new colour lands between the final two stops rather than at the end,
    // so adding one never changes where the gradient starts or finishes.
    stops.splice(stops.length - 1, 0, {
      color: last.color,
      position: previous
        ? Math.round((previous.position + last.position) / 2)
        : 50,
    });
    patchGradient({ stops });
  }

  function removeStop(index: number) {
    patchGradient({
      stops: variant.background.stops.filter((_, i) => i !== index),
    });
  }

  /**
   * Overwrites one scheme with the other's whole variant — seeds and
   * gradient. Meant as a starting point: most palettes want the two schemes
   * *related*, not independently invented, so copying one over and then
   * nudging a couple of colours is normally faster than building both from
   * scratch. Switches the tab to `to` so the result is what's on screen.
   */
  function copyVariant(from: Scheme, to: Scheme) {
    setPalette((prev) => ({
      ...prev,
      [to]: {
        ...prev[from],
        background: {
          ...prev[from].background,
          stops: prev[from].background.stops.map((stop) => ({ ...stop })),
        },
      },
    }));
    setScheme(to);
  }

  async function save() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const input = { name, description, palette };
      const saved = editing
        ? await api.updateCustomTheme(editing.id, input)
        : await api.createCustomTheme(input);
      // A palette you just built is one you want to be wearing.
      onSaved(`custom:${saved.id}`);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Copies the export text to the clipboard.
   *
   * The Clipboard API needs a secure context, and `localhost` counts as one
   * even over plain HTTP — but a browser extension or an older browser can
   * still refuse it. The textarea underneath stays selectable either way, so a
   * failure here is a missed convenience, not a dead end.
   */
  async function copyExport() {
    try {
      await navigator.clipboard.writeText(exportedText);
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      // Nothing to recover into: the text is already visible and selectable.
    }
  }

  async function remove() {
    if (busy || !editing) return;
    setBusy(true);
    setError("");
    try {
      await api.deleteCustomTheme(editing.id);
      // No theme to wear: the server has already put us back on the default if
      // this was the one we had on.
      onSaved();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // The preview is the real derivation, applied inline. A media query is not
  // needed here precisely because the tab already says which scheme this is.
  const previewVars = deriveVars(variant, scheme) as CSSProperties;

  // Exports whatever is on screen right now, saved or not — someone proposing
  // a palette wants to hand it over the moment it looks right, not after an
  // extra step of saving something they may still be tweaking.
  const exportedText = exportTheme({ name, description, palette });

  return (
    <Modal
      title={editing ? "Edit your colors" : "Make your colors"}
      onClose={onClose}
    >
      <div className="stack theme-editor">
        <ErrorMessage>{error}</ErrorMessage>

        <Field label="Name">
          <input
            value={name}
            maxLength={40}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field label="Description" hint="Optional — shows up when you export this theme.">
          <input
            value={description}
            maxLength={200}
            disabled={busy}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        <Segmented<Scheme>
          value={scheme}
          onChange={setScheme}
          options={[
            { value: "light", label: "☀️ Light" },
            { value: "dark", label: "🌙 Dark" },
          ]}
        />

        <div className="row">
          <button
            type="button"
            className="btn btn--small"
            disabled={busy}
            onClick={() => copyVariant("light", "dark")}
          >
            Copy light → dark
          </button>
          <button
            type="button"
            className="btn btn--small"
            disabled={busy}
            onClick={() => copyVariant("dark", "light")}
          >
            Copy dark → light
          </button>
        </div>

        <p className="muted">
          Your device picks light or dark on its own — set up both so it looks
          right either way.
        </p>

        <ThemePreview vars={previewVars} />

        <div className="color-grid">
          {SEEDS.map((seed) => (
            <label key={seed.key} className="color-pick">
              <input
                type="color"
                value={variant[seed.key]}
                disabled={busy}
                onChange={(e) => patch({ [seed.key]: e.target.value })}
              />
              <span>{seed.label}</span>
            </label>
          ))}
        </div>

        <section className="stack">
          <h3 className="appearance__heading">Background</h3>

          <Field label={`Angle: ${variant.background.angle}°`}>
            <input
              type="range"
              min={0}
              max={360}
              step={5}
              value={variant.background.angle}
              disabled={busy}
              onChange={(e) =>
                patchGradient({ angle: Number(e.target.value) })
              }
            />
          </Field>

          <div className="stack stack--tight">
            {variant.background.stops.map((stop, i) => (
              // Stops have no IDs and reorder only by the user's hand, so the
              // index is a stable enough key here.
              <div key={i} className="gradient-stop">
                <input
                  type="color"
                  value={stop.color}
                  disabled={busy}
                  onChange={(e) => setStop(i, { color: e.target.value })}
                />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={stop.position}
                  disabled={busy}
                  aria-label={`Color ${i + 1} position`}
                  onChange={(e) =>
                    setStop(i, { position: Number(e.target.value) })
                  }
                />
                <button
                  type="button"
                  className="btn btn--quiet"
                  disabled={busy || variant.background.stops.length <= MIN_STOPS}
                  onClick={() => removeStop(i)}
                  aria-label={`Remove color ${i + 1}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="btn"
            disabled={busy || variant.background.stops.length >= MAX_STOPS}
            onClick={addStop}
          >
            Add a color
          </button>
          {variant.background.stops.length >= MAX_STOPS ? (
            <p className="muted">That's as many colors as a background can take.</p>
          ) : null}
        </section>

        <section className="stack">
          <h3 className="appearance__heading">Cards</h3>

          {/* The slider carries the stored value directly — no inversion — so
              it only ever moves one way: right is more solid, left is more
              see-through, and the far right is plain opaque. An earlier version
              ran backwards against the underlying number, which made the
              direction impossible to predict. */}
          <Field label="Card background">
            <div className="slider-row">
              <span className="slider-row__end">Translucent</span>
              <input
                type="range"
                min={sliderMin}
                max={MAX_CARD_OPACITY}
                value={cardOpacity}
                disabled={busy}
                aria-label="Card background, translucent to opaque"
                onChange={(e) => {
                  const next = Number(e.target.value);
                  // At the opaque end the field is cleared rather than set to
                  // 100. The two render identically, but an unset palette
                  // exports without a cardOpacity line at all, which is what
                  // keeps a theme that never wanted translucency clean.
                  setCardOpacity(next >= MAX_CARD_OPACITY ? undefined : next);
                }}
              />
              <span className="slider-row__end">Opaque</span>
            </div>
          </Field>
          <p className="muted">
            {cardOpacity >= MAX_CARD_OPACITY
              ? "Cards are solid."
              : `The background shows through your cards. The words on them stay solid.`}
          </p>
        </section>

        <div className="row row--between">
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => setExporting((v) => !v)}
            >
              📤 Export
            </button>
            {editing ? (
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() => void remove()}
              >
                Delete
              </button>
            ) : null}
          </div>

          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => void save()}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {exporting ? (
          <section className="stack theme-share">
            <p className="muted">
              Copy this and send it to whoever's turning it into a theme.
            </p>
            <textarea
              className="theme-share__textarea"
              readOnly
              rows={10}
              value={exportedText}
              onFocus={(e) => e.currentTarget.select()}
            />
            <div className="row">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => void copyExport()}
              >
                {copied ? "Copied!" : "📋 Copy"}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setExporting(false)}
              >
                Close
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * A miniature of the app in the palette being edited.
 *
 * It shows the pieces whose colours are actually being chosen — the background
 * wash, a card, the brand, and the two buckets — so the effect of a change is
 * visible without closing the editor and hunting for it.
 */
function ThemePreview({ vars }: { vars: CSSProperties }) {
  return (
    <div className="theme-preview" style={vars}>
      <div className="theme-preview__card">
        <span className="theme-preview__title">Total:</span>
        <span className="theme-preview__total">$24.50</span>
        <div className="theme-preview__buckets">
          <span className="theme-preview__bucket theme-preview__bucket--spend">
            Spend
          </span>
          <span className="theme-preview__bucket theme-preview__bucket--save">
            Save
          </span>
        </div>
        <span className="theme-preview__btn">Spend some</span>
      </div>
    </div>
  );
}
